import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

function LoanDisbursement() {
  const navigate = useNavigate()
  const [loans, setLoans] = useState([])
  const [selectedLoan, setSelectedLoan] = useState(null)
  const loanSelectRef = useRef(null)
  const [formData, setFormData] = useState({
    noPinjaman: '',
    jumlahPencairan: 0,
    biayaAdmin: 0,
    keterangan: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [namaKoperasi, setNamaKoperasi] = useState('Koperasi')

  useEffect(() => {
    fetchApprovedLoans()
  }, [])

  const handleLoanChange = async (noPinjaman) => {
    const selected = loans.find(l => l.NoPinjaman === noPinjaman)
    setSelectedLoan(selected)

    if (selected) {
      let calculatedAdminFee = 0

      // Fetch loan type to get BiayaAdmin percentage
      try {
        const response = await api.get(`/loans/${noPinjaman}`)
        const loanData = response.data.data
        if (loanData && loanData.KodeJenis) {
          // Get loan type info
          const typeResponse = await api.get('/loans/types')
          const loanTypes = typeResponse.data.data || []
          const loanType = loanTypes.find(t => t.KodeJenis === loanData.KodeJenis)
          if (loanType && loanType.BiayaAdmin) {
            calculatedAdminFee = (selected.Plafond * loanType.BiayaAdmin) / 100
          }
        }
        // If no KodeJenis, admin fee remains 0 - user must enter manually
      } catch (error) {
        console.error('Error fetching loan type:', error)
      }

      setFormData({
        ...formData,
        noPinjaman,
        jumlahPencairan: selected.Plafond || 0,
        biayaAdmin: calculatedAdminFee
      })
    } else {
      setFormData({
        ...formData,
        noPinjaman: '',
        jumlahPencairan: 0,
        biayaAdmin: 0
      })
    }
  }

  useEffect(() => {
    if (loanSelectRef.current && loans.length > 0) {
      $(loanSelectRef.current).select2({
        placeholder: '-- Pilih Pinjaman --',
        allowClear: true,
        width: '100%'
      })

      $(loanSelectRef.current).on('change', function() {
        handleLoanChange($(this).val())
      })

      return () => {
        if (loanSelectRef.current) {
          $(loanSelectRef.current).off('change').select2('destroy')
        }
      }
    }
  }, [loans])

  const fetchApprovedLoans = async () => {
    try {
      const response = await api.get('/loans?status=Disetujui&limit=1000')
      setLoans(response.data.data || [])
    } catch (error) {
      console.error('Error fetching approved loans:', error)
      setError('Gagal mengambil data pinjaman')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const response = await api.post(`/loans/${formData.noPinjaman}/disburse`, {
        jumlahPencairan: formData.jumlahPencairan,
        biayaAdmin: formData.biayaAdmin,
        keterangan: formData.keterangan
      })
      setNamaKoperasi(response.data.data.namaKoperasi || 'Koperasi')
      setPrintData({
        noPinjaman: formData.noPinjaman,
        namaAnggota: selectedLoan?.NamaAnggota,
        nba: selectedLoan?.NBA,
        jumlahPencairan: formData.jumlahPencairan,
        biayaAdmin: formData.biayaAdmin,
        keterangan: formData.keterangan,
        tanggal: new Date().toLocaleDateString('id-ID')
      })
      setShowPrintModal(true)
      setFormData({
        ...formData,
        noPinjaman: '',
        jumlahPencairan: 0,
        biayaAdmin: 0,
        keterangan: ''
      })
      setSelectedLoan(null)
      if (loanSelectRef.current) {
        $(loanSelectRef.current).val(null).trigger('change')
      }
      fetchApprovedLoans()
    } catch (error) {
      setError(error.response?.data?.message || 'Gagal melakukan pencairan')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  return (
    <div className="row">
      <div className="col-md-8">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Pencairan Pinjaman</h3>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger">{error}</div>
              )}

              <div className="form-group">
                <label>Pilih Pinjaman *</label>
                <select
                  ref={loanSelectRef}
                  className="form-control"
                  value={formData.noPinjaman}
                  onChange={(e) => setFormData({ ...formData, noPinjaman: e.target.value })}
                  required
                >
                  <option value="">-- Pilih Pinjaman --</option>
                  {Array.isArray(loans) && loans.map(l => (
                    <option key={l.NoPinjaman} value={l.NoPinjaman}>
                      {l.NoPinjaman} - {l.NamaAnggota} - {formatCurrency(l.Plafond)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedLoan && (
                <div className="alert alert-info">
                  <div><strong>NBA:</strong> {selectedLoan.NBA}</div>
                  <div><strong>Nama:</strong> {selectedLoan.NamaAnggota}</div>
                  <div><strong>Plafond:</strong> {formatCurrency(selectedLoan.Plafond)}</div>
                  <div><strong>Bunga:</strong> {selectedLoan.Bunga}%</div>
                  <div><strong>Tenor:</strong> {selectedLoan.Tenor} bulan</div>
                </div>
              )}

              <div className="form-group">
                <label>Jumlah Pencairan *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.jumlahPencairan}
                  onChange={(e) => setFormData({ ...formData, jumlahPencairan: parseInt(e.target.value) || 0 })}
                  required
                  min="0"
                  max={selectedLoan?.Plafond || 999999999}
                />
              </div>

              <div className="form-group">
                <label>Biaya Admin</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.biayaAdmin}
                  onChange={(e) => setFormData({ ...formData, biayaAdmin: parseInt(e.target.value) || 0 })}
                  min="0"
                  defaultValue="0"
                />
              </div>

              <div className="form-group">
                <label>Keterangan</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.keterangan}
                  onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                />
              </div>
            </div>
            <div className="card-footer">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Memproses...
                  </>
                ) : (
                  <>
                    <i className="fas fa-money-bill-wave mr-2"></i>
                    Cairkan Pinjaman
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-default ml-2"
                onClick={() => navigate('/loans/reguler')}
              >
                Batal
              </button>
            </div>
          </form>
        </div>
      </div>

      {showPrintModal && printData && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-success">
                <h5 className="modal-title">Bukti Pencairan Pinjaman</h5>
                <button type="button" className="close text-white" onClick={() => setShowPrintModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPrintModal(false)}>
                  Tutup
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const receiptContent = `
================================
  BUKTI PENCAIRAN PINJAMAN
================================
No Pinjaman: ${printData.noPinjaman}
Tanggal    : ${printData.tanggal}
---------------------------------
DATA ANGGOTA
---------------------------------
NBA        : ${printData.nba}
Nama       : ${printData.namaAnggota}
---------------------------------
DETAIL PENCAIRAN
---------------------------------
Jumlah Pencairan: Rp ${parseFloat(printData.jumlahPencairan).toLocaleString('id-ID')}
Biaya Admin     : Rp ${parseFloat(printData.biayaAdmin).toLocaleString('id-ID')}
---------------------------------
Total Diterima  : Rp ${(parseFloat(printData.jumlahPencairan) - parseFloat(printData.biayaAdmin)).toLocaleString('id-ID')}
---------------------------------
KETERANGAN
---------------------------------
${printData.keterangan || '-'}
================================
  Tanda Tangan

(${namaKoperasi})
================================
`
                    const printWindow = window.open('', '_blank')
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Bukti Pencairan Pinjaman</title>
                          <style>
                            body {
                              font-family: 'Courier New', monospace;
                              font-size: 12px;
                              margin: 0;
                              padding: 10px;
                              white-space: pre;
                              line-height: 1.2;
                            }
                          </style>
                        </head>
                        <body>${receiptContent}</body>
                      </html>
                    `)
                    printWindow.document.close()
                    printWindow.print()
                  }}
                >
                  <i className="fas fa-print mr-1"></i> Cetak
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoanDisbursement
